import { MainLayoutContainer } from "components/Layout";
import { FC, useState, useEffect, useMemo } from "react";
import * as Yup from "yup";
import {
  Avatar,
  Box,
  Button,
  Grid2 as Grid,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  TextField,
  Divider,
} from "@mui/material";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useAuthUser } from "hooks/useAuthUser";
import {
  useGetUserProfileStudentLazyQuery,
  useGetOrganizationByUserIdLazyQuery,
} from "graphql/__generated__/hooks";
import { GET_USER_BY_ID } from "graphql/user/user.query";
import { UPDATE_USER } from "graphql/user/user.mutation";
import { GET_USER_BADGES } from "graphql/badge/badge.query";
import { BadgeDisplay } from "components/Badge/BadgeDisplay";
import { BadgeAnimationModal } from "components/Badge/BadgeAnimationModal";
import { Badge } from "types/badge";
import { VisuallyHiddenInput } from "components/Input";
import { Upload } from "@mui/icons-material";
import { useAppDispatch } from "hooks/useAppDispatch";
import { Messages, NotificationSeverity } from "helpers/constants";
import { uploadFile } from "reduxStore/files/thunks";
import { useFormik } from "formik";
import { updateUserThunk, checkLoggedUserThunk } from "reduxStore/auth/thunks";
import { ROLES, User } from "types/user";
import { ICreateOrUpdateUserCredentials } from "types/auth";
import ResetPasswordModal from "modals/ResetPasswordModal/ResetPasswordModal";
import { useNotifications } from "hooks/useNotifications";
import { downloadStudentEducationRecords } from "services/educationRecords";
import * as React from "react";
import { isRunningInNativeApp, isMobile } from "../../utils/UserDevice";

export const initialValues = {
  name: "",
  email: "",
  phone: "",
  sms_opt_in: false,
};
type ProfileFormValues = typeof initialValues;

export const getValidationSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    phone: Yup.string()
      .nullable()
      .test(
        "phone-format",
        "Use a valid phone number (include country code, ex: +15551234567)",
        (value) => {
          if (!value) return true;
          return /^\+?[1-9]\d{7,14}$/.test(value.replace(/\s+/g, ""));
        }
      ),
    // Email validation removed since it's read-only
  });

export const Profile: FC = () => {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [errorImage, setErrorImage] = useState("");
  const [passwordModal, setPasswordModal] = useState(false);
  const [selectedBadges, setSelectedBadges] = useState<Badge[]>([]);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const { user } = useAuthUser();
  const { data: userBadgesData } = useQuery(GET_USER_BADGES, {
    variables: { userId: user?.user_id || "" },
    skip: !user?.user_id,
  });
  const [getUserData, { data: userData }] = useLazyQuery(GET_USER_BY_ID);
  const [getUserProfileStudent, { data: userProfileStudentData }] =
    useGetUserProfileStudentLazyQuery();
  const [getOrganization, { data: organizationData }] =
    useGetOrganizationByUserIdLazyQuery();
  const [updateUser] = useMutation(UPDATE_USER);
  const dispatch = useAppDispatch();
  const { notify } = useNotifications();
  const [downloadingRecords, setDownloadingRecords] = useState(false);

  const handleDownloadRecords = async () => {
    setDownloadingRecords(true);
    try {
      await downloadStudentEducationRecords("me");
    } catch {
      notify({
        message: "Couldn't download your records. Try again shortly.",
        severity: NotificationSeverity.error,
      });
    } finally {
      setDownloadingRecords(false);
    }
  };

  const userProfile = useMemo(() => {
    if (user?.role === ROLES.STUDENT) {
      return userProfileStudentData?.graduation_class_student?.[0]?.student;
    }
    return userData?.user_by_pk;
  }, [user, userData, userProfileStudentData]);

  useEffect(() => {
    if (userProfile?.picture_url) {
      setAvatar(userProfile.picture_url);
    }
  }, [userProfile]);

  useEffect(() => {
    if (user?.user_id && user?.role === ROLES.STUDENT) {
      getUserProfileStudent({
        variables: {
          userId: user.user_id,
        },
      });
    }
    if (user?.user_id) {
      getUserData({
        variables: {
          userId: user.user_id,
        },
      });
      getOrganization({
        variables: {
          userId: user.user_id,
        },
      });
      setAvatar(userData?.user_by_pk?.picture_url ?? "");
    }
  }, [user]);

  const userInfo = useMemo(() => userData?.user_by_pk, [userData]);
  const organization = useMemo(() => {
    if (user?.role === ROLES.STUDENT) {
      return userProfileStudentData?.graduation_class_student?.[0]
        ?.graduation_class?.program?.organization;
    }
    return organizationData?.organization?.[0];
  }, [organizationData, userProfileStudentData]);

  const gradClass = useMemo(() => {
    if (user?.role === ROLES.STUDENT) {
      return userProfileStudentData?.graduation_class_student?.[0]
        ?.graduation_class;
    }
    return null;
  }, [userProfileStudentData]);

  const UploadFile = async () => {
    if (avatarFile) {
      setUploading(true);
      const result = await dispatch(uploadFile(avatarFile));
      if (result.meta.requestStatus === "rejected") {
        setErrorImage(Messages.errorImageUpload);
        return "ERROR";
      }
      setUploading(false);
      return result?.payload?.filePath;
    } else {
      return null;
    }
  };

  const formik = useFormik<ProfileFormValues>({
    initialValues: {
      ...initialValues,
      name: userInfo?.name || user?.name || "",
      email: userInfo?.email || user?.email || "",
      phone: userInfo?.phone || "",
      sms_opt_in: Boolean(userInfo?.sms_opt_in),
    },
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const normalizedCurrentName = userInfo?.name?.trim() || "";
        const normalizedNewName = values.name?.trim() || "";
        const shouldUpdateCognito =
          Boolean(user?.user_id) &&
          Boolean(userInfo?.email) &&
          normalizedCurrentName !== normalizedNewName;

        const reqValues: ICreateOrUpdateUserCredentials = {
          user_id: user?.user_id,
          name: normalizedNewName,
          email: userInfo?.email || values.email, // Use original email, don't allow changes
          role: userInfo?.role ?? "",
        };
        const picture_url = await UploadFile();
        if (picture_url === "ERROR") {
          notify({
            message: Messages.errorImageUpload || "Error uploading image",
            severity: NotificationSeverity.error,
          });
          return;
        }
        setLoading(true);

        let payload: any = "success";
        if (shouldUpdateCognito) {
          // Update Cognito only when immutable identity fields change
          const response = await dispatch(updateUserThunk(reqValues));
          payload = response.payload;
        }

        if (payload === "success") {
          try {
            // Update database via GraphQL mutation
            // Always update name and picture_url (if provided) in the database
            const updateData: Partial<User> = {
              name: reqValues.name?.toString(),
              phone: values.phone?.trim() || null,
              sms_opt_in: Boolean(values.sms_opt_in),
            };
            if (picture_url) {
              updateData.picture_url = picture_url;
            }

            await updateUser({
              variables: {
                id: reqValues.user_id,
                user: updateData as any,
              },
            });

            // Refetch user data to update the UI
            if (user?.user_id) {
              await getUserData({
                variables: { userId: user.user_id },
              });
            }

            // Refresh Redux user state to update Header
            await dispatch(checkLoggedUserThunk());

            notify({
              message: Messages.successUserUpdated,
              severity: NotificationSeverity.success,
            });
          } catch (graphqlError: any) {
            console.error("Error updating user in database:", graphqlError);
            notify({
              message:
                graphqlError.message || "Error updating profile in database",
              severity: NotificationSeverity.error,
            });
          }
        } else {
          // Handle error case - payload contains error message
          notify({
            message: Messages.errorUserUpdated,
            severity: NotificationSeverity.error,
          });
        }
      } catch (error: any) {
        console.error("Error in profile update:", error);
        notify({
          message: error.message || "An unexpected error occurred",
          severity: NotificationSeverity.error,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const onUploadFile = (event: React.BaseSyntheticEvent) => {
    const file = event.target.files[0];
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setErrorImage("Invalid file type: only PNG and JPEG are allowed");
      return;
    }
    if (file.size > 1024 * 1024 * 10) {
      setErrorImage("File size is too large: max size is 10MB");
      return;
    }
    if (errorImage !== "") {
      setErrorImage("");
    }
    setAvatar(URL.createObjectURL(file));
    setAvatarFile(file);
  };

  return (
    <MainLayoutContainer title="Profile Settings">
      <Box>
        <Stack direction="row" alignItems="center">
          <Avatar src={avatar ?? undefined} sx={{ width: 100, height: 100 }} />
          <Box ml={2}>
            <Box mb={1} fontWeight={600} fontSize={16}>
              Profile Picture
            </Box>
            {isMobile() || isRunningInNativeApp() ? (
              <Typography variant="body2" color="textSecondary">
                To change profile picture, please use a desktop device.
              </Typography>
            ) : (
              <Button
                disabled={uploading}
                component="label"
                role={undefined}
                variant="outlined"
                tabIndex={-1}
                startIcon={<Upload />}
              >
                Upload image
                <VisuallyHiddenInput type="file" onChange={onUploadFile} />
              </Button>
            )}
            {errorImage !== "" && (
              <Box mt={1} sx={{ color: "red" }}>
                {errorImage}
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      <Box>
        <Divider sx={{ my: 3 }} />
        <Box sx={{ my: 1 }}>
          <Stack
            direction={{ xs: "row" }}
            justifyContent={{ xs: "space-between" }}
            spacing={1}
          >
            <Typography variant="body1" sx={{ textAlign: { xs: "left" } }}>
              <strong>Role:</strong> {userInfo?.role}
            </Typography>
          </Stack>

          {organization && (
            <Typography variant="body1" sx={{ textAlign: { xs: "left" } }}>
              <strong>Organization:</strong>{" "}
              {user?.role === ROLES.STUDENT
                ? organization?.name
                : organizationData?.organization
                    ?.map((org) => org.name)
                    .join(", ")}
            </Typography>
          )}
          {gradClass && (
            <Typography variant="body1" sx={{ textAlign: { xs: "left" } }}>
              <strong>Graduation Class:</strong> {gradClass?.name}
            </Typography>
          )}
        </Box>

        {userBadgesData?.user_badge && userBadgesData.user_badge.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Badges
            </Typography>
            <BadgeDisplay
              badges={userBadgesData.user_badge as any}
              size="medium"
              onBadgeClick={(badge) => {
                setSelectedBadges([badge]);
                setShowBadgeModal(true);
              }}
            />
          </>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" color="text.secondary">
            Personal Information
          </Typography>

          <Box mb={3} mt={4}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Name"
              size="small"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name ? formik.errors.name : ""}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              size="small"
              value={formik.values.email}
              disabled
              InputProps={{
                readOnly: true,
              }}
              helperText="Email cannot be changed"
            />
          </Box>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" color="text.secondary">
            SMS Notifications
          </Typography>
          <Box sx={{ mt: 3, mb: 2 }}>
            <TextField
              fullWidth
              id="phone"
              name="phone"
              label="Phone Number"
              size="small"
              placeholder="+15551234567"
              value={formik.values.phone || ""}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={
                formik.touched.phone && formik.errors.phone
                  ? formik.errors.phone
                  : "Include country code for SMS delivery"
              }
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formik.values.sms_opt_in)}
                onChange={(event) =>
                  formik.setFieldValue("sms_opt_in", event.target.checked)
                }
                name="sms_opt_in"
              />
            }
            label="Receive feedback updates via SMS"
          />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              Save changes
            </Button>
          </Box>
        </form>

        <Divider sx={{ my: 3 }} />
        <Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" color="text.secondary">
                Do you want to change password?
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setPasswordModal(true)}
                >
                  Change password
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        {user?.role === ROLES.STUDENT && (
          <>
            <Divider sx={{ my: 3 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  Your education records
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Download a copy of the feedback, comments, survey answers,
                  and mentoring records VetNebula holds about you.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={downloadingRecords}
                    onClick={handleDownloadRecords}
                  >
                    Download my records
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
      {passwordModal && (
        <ResetPasswordModal
          passwordModal={passwordModal}
          handlePasswordModalClose={() => setPasswordModal(false)}
        />
      )}
      <BadgeAnimationModal
        badges={selectedBadges}
        open={showBadgeModal}
        onClose={() => {
          setShowBadgeModal(false);
          setSelectedBadges([]);
        }}
      />
    </MainLayoutContainer>
  );
};
